/* eslint-disable no-param-reassign */
import { CHROMA_MAX_OPACITY } from 'constant'
import { VideoAsset } from 'models/Asset'
import React, { useRef, useEffect, useState } from 'react'
import shaders from './shaders'

// This hook is a high-performance bridge between the video decoder and the graphics processing unit (GPU).
// While a regular Canvas 2D renders frames on the CPU,
// this code offloads the processing of 8 million pixels (in 4K) to the graphics card.

interface GLResources {
  prog: WebGLProgram;
  vb: WebGLBuffer;
  tex: WebGLTexture;
  vs: WebGLShader;
  fs: WebGLShader;
}

type HTMLVideoElementWithCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: (now: number, metadata: Record<string, unknown>) => void) => number;
  cancelVideoFrameCallback?: (id: number) => void;
};

type Props = {
  asset: VideoAsset;
  videoRef: React.MutableRefObject<HTMLVideoElement | null> | null;
  width: number;
  height: number;
  boundingRect: {
    width: number,
    height: number,
  }
}

const shaderEntry = shaders.find(s => s.name === 'chromakey')
const shaderSource = shaderEntry ? shaderEntry.glsl : ''
const MAX_PREVIEW_WIDTH = 1280

function createGLResources(gl: WebGLRenderingContext): GLResources | null {
  const vs = gl.createShader(gl.VERTEX_SHADER)
  const fs = gl.createShader(gl.FRAGMENT_SHADER)
  const prog = gl.createProgram()
  const vb = gl.createBuffer()
  const tex = gl.createTexture()

  if (!vs || !fs || !prog || !vb || !tex || !shaderSource) {
    return null
  }

  gl.shaderSource(vs, 'attribute vec2 c; void main(void) { gl_Position=vec4(c, 0.0, 1.0); }')
  gl.compileShader(vs)

  gl.shaderSource(fs, shaderSource)
  gl.compileShader(fs)

  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.useProgram(prog)

  gl.bindBuffer(gl.ARRAY_BUFFER, vb)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([ -1, 1, -1, -1, 1, -1, 1, 1 ]),
    gl.STATIC_DRAW
  )

  const coordLoc = gl.getAttribLocation(prog, 'c')
  gl.vertexAttribPointer(coordLoc, 2, gl.FLOAT, false, 0, 0)
  gl.enableVertexAttribArray(coordLoc)

  gl.bindTexture(gl.TEXTURE_2D, tex)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)

  return { prog, vb, tex, vs, fs }
}


export const useVideoChromaKey = ({ asset, videoRef, width, height, boundingRect }: Props) => {
  const settings = asset?.settings || {}
  const innerRef = useRef<HTMLVideoElement | null>(null)
  const activeVideoRef = videoRef || innerRef
  const [ canvas ] = useState(() => {
    const c = document.createElement('canvas')
    const scale = width > MAX_PREVIEW_WIDTH ? MAX_PREVIEW_WIDTH / width : 1

    // reduce the canvas size
    const reduceWidth = Math.round(width * scale)
    const reduceHeight = Math.round(height * scale)
    c.width = boundingRect?.width ?? reduceWidth
    c.height = boundingRect?.height ?? reduceHeight
    return c
  })

  const stateRef = useRef({
    isRunning: false,
    frameId: 0,
    isProcessing: false,
  })

  useEffect(() => {
    const video = activeVideoRef.current as HTMLVideoElementWithCallback
    if (!video || !canvas || !settings.isChromaKeyEnabled) return undefined

    const gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
      depth: false,
      preserveDrawingBuffer: true,
    })
    if (!gl) return undefined

    const resources = createGLResources(gl)
    if (!resources) return undefined

    const { prog, tex, vb, vs, fs } = resources

    const locs = {
      tex: gl.getUniformLocation(prog, 'tex'),
      texWidth: gl.getUniformLocation(prog, 'texWidth'),
      texHeight: gl.getUniformLocation(prog, 'texHeight'),
      keyColor: gl.getUniformLocation(prog, 'keyColor'),
      similarity: gl.getUniformLocation(prog, 'similarity'),
      smoothness: gl.getUniformLocation(prog, 'smoothness'),
      spill: gl.getUniformLocation(prog, 'spill'),
      opacity: gl.getUniformLocation(prog, 'opacity'),
    }

    const drawFrame = () => {
      if (!video || video.videoWidth === 0) return

      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        gl.viewport(0, 0, canvas.width, canvas.height)
      }

      gl.useProgram(prog)
      gl.bindTexture(gl.TEXTURE_2D, tex)

      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
      /*
       * Here, the video card directly reads the current frame from the video element.
       * Use the RGBA format because it's native to modern GPUs, avoiding unnecessary on-the-fly format conversions.
       * */
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, video)

      const { r, g, b } = settings.chromaKeyColor
      gl.uniform1i(locs.tex, 0)
      gl.uniform1f(locs.texWidth, canvas.width)
      gl.uniform1f(locs.texHeight, canvas.height)
      gl.uniform3f(locs.keyColor, r / 255, g / 255, b / 255)
      gl.uniform1f(locs.similarity, settings.similarity / 1000)
      gl.uniform1f(locs.smoothness, settings.smoothness / 1000)
      gl.uniform1f(locs.spill, settings.spillReduction / 1000)
      gl.uniform1f(locs.opacity, settings.chromaKeyOpacity / CHROMA_MAX_OPACITY)

      gl.drawArrays(gl.TRIANGLE_FAN, 0, 4)
    }

    const processFrame = () => {
      if (!stateRef.current.isRunning || !video || video.ended) return

      if (stateRef.current.isProcessing) {
        scheduleNext()
        return
      }

      stateRef.current.isProcessing = true
      drawFrame()
      stateRef.current.isProcessing = false
      scheduleNext()
    }

    function scheduleNext() {
      if (video.requestVideoFrameCallback) {
        stateRef.current.frameId = video.requestVideoFrameCallback(processFrame)
      } else {
        stateRef.current.frameId = requestAnimationFrame(processFrame)
      }
    }

    const start = () => {
      if (!stateRef.current.isRunning) {
        stateRef.current.isRunning = true
        scheduleNext()
      }
    }

    const stop = () => {
      stateRef.current.isRunning = false
      if (video.cancelVideoFrameCallback) {
        video.cancelVideoFrameCallback(stateRef.current.frameId)
      } else {
        cancelAnimationFrame(stateRef.current.frameId)
      }
    }

    const handleSeeked = () => {
      if (!video.paused) return
      drawFrame()
    }

    video.addEventListener('playing', start)
    video.addEventListener('pause', stop)
    video.addEventListener('seeked', handleSeeked)
    video.addEventListener('canplay', drawFrame)

    drawFrame()
    if (!video.paused) start()

    return () => {
      stop()
      video.removeEventListener('playing', start)
      video.removeEventListener('pause', stop)
      video.removeEventListener('seeked', handleSeeked)
      video.removeEventListener('canplay', drawFrame)

      gl.deleteTexture(tex)
      gl.deleteBuffer(vb)
      gl.deleteShader(vs)
      gl.deleteShader(fs)
      gl.deleteProgram(prog)
    }
  }, [ canvas, settings, activeVideoRef ])

  if (!__CFG__.IS_CHROMAKEY_ENABLED || !activeVideoRef || !settings.isChromaKeyEnabled) {
    return null
  }

  return canvas
}
